import React, { useContext } from "react";
import styled from "styled-components";
import { Link, useLocation } from "react-router-dom";
import { XOutlined, FacebookFilled, LinkedinFilled } from "@ant-design/icons";
import Logo from "./general/Logo";
import {
  APP_NAME,
  COMPANY_NAME,
  facebookLink,
  linkedinLink,
  twitterLink,
  tikTokLink,
} from "../helpers/constants";
import { APP_PRIVACY_ROUTE, APP_TERMS_ROUTE } from "../helpers/routeNames";
import { AuthContext } from "../context/AuthProvider";
import { useBreakpoints } from "../lib/breakpoints";

export default function NavFooter() {
  const { user, userInfo } = useContext(AuthContext);
  const location = useLocation();
  const { isMobile } = useBreakpoints();
  const isPublicLeaseListing = location?.pathname?.startsWith("/lease/");
  const showLandlordListingCta = !user && isPublicLeaseListing;
  const landlordCtaPrimaryTo = isMobile ? "/signup" : "/?signup=";
  const isWashington =
    user &&
    (userInfo?.state === "WA" ||
      userInfo?.address?.state === "WA" ||
      userInfo?.primaryState === "WA");
  return (
    <Footer className="footer">
      <FooterContainer>
        {showLandlordListingCta && (
          <ListingFooterCta>
            <ListingFooterCtaText>
              <ListingFooterCtaTitle>
                Want a listing like this of your own?
              </ListingFooterCtaTitle>
              <ListingFooterCtaSubtitle>
                LandlordOps creates Smart Vacancy pages with AI pre-screening
                and tour booking - so you only talk to qualified prospects.
              </ListingFooterCtaSubtitle>
            </ListingFooterCtaText>
            <ListingFooterCtaActions>
              <ListingFooterCtaPrimary to={"/"}>
                Get LandlordOps
              </ListingFooterCtaPrimary>
              <ListingFooterCtaSecondary to="/for-self-managing-landlords">
                Learn more
              </ListingFooterCtaSecondary>
            </ListingFooterCtaActions>
          </ListingFooterCta>
        )}

        <FooterTop>
          <FooterBrand>
            <LogoWrapper>
              <Logo size="medium" displayMode="both" color="white" />
            </LogoWrapper>
            <FooterTagline>
              {`Put your entire rental portfolio on autopilot with the full LandlordOps OS on every plan - handling automated rent collection, Schedule E tax prep, vendor bidding, and 24/7 AI tenant triage. Upgrade only when you grow your door\u00A0count.`}
            </FooterTagline>
            <SocialMediaSection>
              <SocialMediaTitle>Follow Us</SocialMediaTitle>
              <SocialMediaLinks>
                <SocialMediaLink
                  href={twitterLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow us on X"
                >
                  <XOutlined />
                </SocialMediaLink>
                <SocialMediaLink
                  href={facebookLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow us on Facebook"
                >
                  <FacebookFilled />
                </SocialMediaLink>
                <SocialMediaLink
                  href={linkedinLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow us on LinkedIn"
                >
                  <LinkedinFilled />
                </SocialMediaLink>
                <SocialMediaLink
                  href={tikTokLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow us on TikTok"
                >
                  <TikTokIcon />
                </SocialMediaLink>
              </SocialMediaLinks>
            </SocialMediaSection>
          </FooterBrand>

          <FooterLinks>
            <FooterColumn>
              <FooterColumnTitle>Product</FooterColumnTitle>
              <FooterLinkList>
                {user ? (
                  <>
                    <FooterLinkItem>
                      <FooterLink to="/dashboard" className="icon-hover">
                        Dashboard
                      </FooterLink>
                    </FooterLinkItem>
                    <FooterLinkItem>
                      <FooterLink to="/tenants" className="icon-hover">
                        Tenants
                      </FooterLink>
                    </FooterLinkItem>
                    <FooterLinkItem>
                      <FooterLink to="/units" className="icon-hover">
                        Doors
                      </FooterLink>
                    </FooterLinkItem>
                  </>
                ) : (
                  <>
                    <FooterLinkItem>
                      <FooterLink to="/#pricing" className="icon-hover">
                        Pricing
                      </FooterLink>
                    </FooterLinkItem>
                    <FooterLinkItem>
                      <FooterLink
                        to="/#product-features"
                        className="icon-hover"
                      >
                        Platform
                      </FooterLink>
                    </FooterLinkItem>
                  </>
                )}
              </FooterLinkList>
            </FooterColumn>
            <FooterColumn>
              <FooterColumnTitle>Support</FooterColumnTitle>
              <FooterLinkList>
                <FooterLinkItem>
                  <FooterLink to="/contact" className="icon-hover">
                    Contact
                  </FooterLink>
                </FooterLinkItem>
                {!user && (
                  <FooterLinkItem>
                    <FooterLink
                      to="/for-self-managing-landlords"
                      className="icon-hover"
                    >
                      For self-managing landlords
                    </FooterLink>
                  </FooterLinkItem>
                )}
              </FooterLinkList>
              <SupportHoursSection>
                <SupportHoursTitle>Business Hours</SupportHoursTitle>
                <SupportHoursText>Daily Support Available</SupportHoursText>
                <SupportNote>
                  Limited availability Fri. sunset - Sat. sunset
                </SupportNote>
              </SupportHoursSection>
            </FooterColumn>
            <FooterColumn>
              <FooterColumnTitle>Legal</FooterColumnTitle>
              <FooterLinkList>
                <FooterLinkItem>
                  <FooterLink to={APP_PRIVACY_ROUTE} className="icon-hover">
                    Privacy Policy
                  </FooterLink>
                </FooterLinkItem>
                <FooterLinkItem>
                  <FooterLink to={APP_TERMS_ROUTE} className="icon-hover">
                    Terms of Service
                  </FooterLink>
                </FooterLinkItem>
                {isWashington && (
                  <FooterLinkItem>
                    <FooterLink
                      to={`${APP_PRIVACY_ROUTE}#consumer-health`}
                      className="icon-hover"
                    >
                      Your Privacy Rights: Washington Consumer Health Data
                      Privacy Policy
                    </FooterLink>
                  </FooterLinkItem>
                )}
              </FooterLinkList>
            </FooterColumn>
          </FooterLinks>
        </FooterTop>

        <FooterBottom>
          <FooterCopyright>
            © {new Date().getFullYear()} {COMPANY_NAME}. All rights reserved.
          </FooterCopyright>
        </FooterBottom>
      </FooterContainer>
    </Footer>
  );
}

const Footer = styled.footer`
  background: linear-gradient(165deg, #1f4d28 0%, #1a3d22 45%, #16351c 100%);
  color: var(--color-text-white);
  padding: 5rem 0 2.5rem 0;
  border-top: 1px solid rgba(44, 95, 45, 0.1);
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(44, 95, 45, 0.3),
      transparent
    );
  }
`;

const FooterContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
`;

const ListingFooterCta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  padding: 1.25rem 1.5rem;
  margin-bottom: 2.5rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    padding: 1.1rem 1.1rem;
    gap: 1rem;
  }
`;

const ListingFooterCtaText = styled.div`
  min-width: 0;
  text-align: left;

  @media (max-width: 768px) {
    text-align: center;
  }
`;

const ListingFooterCtaTitle = styled.div`
  font-family: var(--font-primary);
  font-weight: 800;
  font-size: 1.05rem;
  letter-spacing: -0.01em;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 0.25rem;
`;

const ListingFooterCtaSubtitle = styled.div`
  font-family: var(--font-secondary);
  color: rgba(255, 255, 255, 0.78);
  line-height: 1.45;
  font-size: 0.95rem;
  max-width: 58ch;

  @media (max-width: 768px) {
    max-width: none;
  }
`;

const ListingFooterCtaActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
  }
`;

const ListingFooterCtaPrimary = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.1rem;
  border-radius: 12px;
  font-weight: 800;
  font-family: var(--font-primary);
  background: rgba(255, 255, 255, 0.96);
  color: var(--color-primary-dark) !important;
  border: 1px solid rgba(255, 255, 255, 0.22);
  text-decoration: none;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  white-space: nowrap;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.22);
    text-decoration: none;
  }

  &:visited {
    color: var(--color-primary-dark) !important;
  }
`;

const ListingFooterCtaSecondary = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.1rem;
  border-radius: 12px;
  font-weight: 700;
  font-family: var(--font-primary);
  background: transparent;
  color: rgba(255, 255, 255, 0.9) !important;
  border: 1px solid rgba(255, 255, 255, 0.18);
  text-decoration: none;
  transition: transform 0.15s ease, background 0.15s ease;
  white-space: nowrap;

  &:hover {
    transform: translateY(-1px);
    background: rgba(255, 255, 255, 0.06);
    text-decoration: none;
  }

  &:visited {
    color: rgba(255, 255, 255, 0.9) !important;
  }
`;

const FooterTop = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 5rem;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const FooterBrand = styled.div`
  max-width: 350px;

  @media (max-width: 768px) {
    text-align: center;
    margin: 0 auto;

    /* Override Logo component styling for mobile centering */
    > div {
      justify-content: center !important;
    }

    /* Ensure the logo container is centered */
    .logo-container {
      justify-content: center !important;
    }
  }
`;

const LogoWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const FooterTagline = styled.p`
  font-size: 1.125rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
  margin-top: 1.5rem;
  font-family: var(--font-secondary);
  letter-spacing: -0.005em;
  font-weight: 400;
  text-align: left;

  @media (max-width: 768px) {
    text-align: center;
  }
`;

const SocialMediaSection = styled.div`
  margin-top: 2rem;

  @media (max-width: 768px) {
    text-align: center;
  }
`;

const SocialMediaTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 1rem;
  font-family: var(--font-primary);
  letter-spacing: -0.01em;
  text-align: left;

  @media (max-width: 768px) {
    text-align: center;
  }
`;

const SocialMediaLinks = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: flex-start;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const SocialMediaLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.25rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  text-decoration: none;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    color: var(--color-primary-light);
    transform: translateY(-2px);
  }

  &:focus {
    outline: 2px solid var(--color-primary-light);
    outline-offset: 2px;
  }
`;

const FooterLinks = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 3rem;
  justify-items: start;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2.5rem;
    justify-items: center;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const FooterColumn = styled.div`
  @media (max-width: 768px) {
    text-align: center !important;
    width: 100%;
    margin: 0 auto;
  }
`;

const FooterColumnTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-text-white);
  margin-bottom: 2rem;
  font-family: var(--font-primary);
  letter-spacing: -0.01em;
`;

const FooterLinkList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FooterLinkItem = styled.li`
  margin-bottom: 1rem;
`;

const FooterLink = styled(Link)`
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  font-size: 1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: var(--font-secondary);
  letter-spacing: -0.005em;
  font-weight: 400;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 1px;
    background: var(--color-primary-light);
    transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &:hover {
    color: var(--color-primary-light);
    transform: translateX(4px);

    &::before {
      width: 100%;
    }
  }
`;

const SupportHoursSection = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border-left: 3px solid var(--color-primary-light);
`;

const SupportHoursTitle = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.875rem;
  font-family: var(--font-secondary);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
`;

const SupportHoursText = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.95rem;
  font-family: var(--font-secondary);
  line-height: 1.5;
  margin-bottom: 0.25rem;
`;

const SupportNote = styled.div`
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.8rem;
  font-family: var(--font-secondary);
  line-height: 1.4;
  font-style: italic;
`;

const FooterBottom = styled.div`
  display: flex;
  align-items: center;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const FooterCopyright = styled.p`
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.6);
  font-family: var(--font-secondary);
  letter-spacing: -0.005em;
  font-weight: 400;
  text-align: center;
  margin: auto;
`;

// TikTok Icon Component (Ant Design doesn't have a TikTok icon)
const TikTokIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    width="1.25rem"
    height="1.25rem"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);
